import { useState } from "react";
import { Bell, BellOff, CalendarPlus, CornerDownRight, Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { CopyTextContextMenu } from "@/components/tasks/task-detail/CopyTextContextMenu";
import { HoverCopy } from "@/components/tasks/task-detail/HoverCopy";
import type { StageKey, Task } from "../../mock/types";
import { checklistPct, stagesByKey, STATUS_VARIANT } from "../../mock/helpers";
import { PriorityBadge, TicketCode } from "../bits";
import { DrawerShell } from "./DrawerShell";
import { MetaRail } from "./MetaRail";
import { ChecklistSection } from "./ChecklistSection";
import { GithubSection } from "./GithubSection";
import { AttachmentsSection } from "./AttachmentsSection";
import { CommentsSection } from "./CommentsSection";
import { HistorySection } from "./HistorySection";

export function TaskDetailDrawer({
	task,
	parentTask,
	childTasks,
	onClose,
	onToggleChecklist,
	onAddComment,
	onStatusChange,
	onOpenTask,
	onAddSubtask,
	escEnabled = true,
}: {
	task: Task | null;
	parentTask: Task | null;
	childTasks: Task[];
	onClose: () => void;
	onToggleChecklist: (itemId: string) => void;
	onAddComment: (body: string) => void;
	onStatusChange: (status: StageKey) => void;
	onOpenTask: (task: Task) => void;
	onAddSubtask: () => void;
	escEnabled?: boolean;
}) {
	const [subscribed, setSubscribed] = useState(true);

	const open = !!task;
	const stage = task ? stagesByKey.get(task.status) : null;
	const childDone = childTasks.filter((t) => t.status === "done").length;

	return (
		<DrawerShell open={open} onClose={onClose} level="base" escEnabled={escEnabled}>
			{task && (
					<>
						<header className="flex items-center justify-between gap-3 border-b border-border px-5 py-3">
							<div className="flex items-center gap-2">
								{task.ticket_code && (
									<TicketCode code={task.ticket_code} />
								)}
								{stage && (
									<Badge
										variant={STATUS_VARIANT[task.status]}
									>
										{stage.name}
									</Badge>
								)}
								<PriorityBadge priority={task.priority} />
							</div>
							<div className="flex items-center gap-1">
								<Button
									variant="ghost"
									size="sm"
									onClick={() => setSubscribed((s) => !s)}
								>
									{subscribed ? (
										<Bell className="h-4 w-4" />
									) : (
										<BellOff className="h-4 w-4" />
									)}
									{subscribed ? "Watching" : "Watch"}
								</Button>
								<Button variant="ghost" size="sm">
									<CalendarPlus className="h-4 w-4" />
									Calendar
								</Button>
								<button
									type="button"
									onClick={onClose}
									className="rounded-md p-1.5 text-muted hover:bg-muted-subtle hover:text-foreground"
									aria-label="Close"
								>
									<X className="h-4 w-4" />
								</button>
							</div>
						</header>

						<CopyTextContextMenu
							title={task.title}
							description={task.description}
							className="grid flex-1 grid-cols-1 overflow-y-auto md:grid-cols-[1fr_300px]"
						>
							<div className="min-w-0 space-y-6 px-5 py-5">
								{parentTask && (
									<button
										type="button"
										onClick={() => onOpenTask(parentTask)}
										className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
									>
										<CornerDownRight className="h-3 w-3" />
										Part of{" "}
										{parentTask.ticket_code ??
											parentTask.title}
									</button>
								)}

								<HoverCopy text={task.title} label="Title">
									<h2 className="pr-9 text-xl font-semibold text-foreground">
										{task.title}
									</h2>
								</HoverCopy>

								<div>
									<h3 className="mb-1.5 text-sm font-semibold text-foreground">
										Description
									</h3>
									<HoverCopy
										text={task.description ?? ""}
										label="Description"
									>
										<p className="pr-9 text-sm text-foreground/90">
											{task.description ?? (
												<span className="text-muted">
													No description.
												</span>
											)}
										</p>
									</HoverCopy>
								</div>

								<section>
									<div className="mb-2 flex items-center gap-2">
										<h3 className="text-sm font-semibold text-foreground">
											Subtasks
										</h3>
										{childTasks.length > 0 && (
											<span className="text-xs text-muted">
												{childDone}/{childTasks.length}
											</span>
										)}
										<Button
											variant="ghost"
											size="sm"
											className="ml-auto"
											onClick={onAddSubtask}
										>
											<Plus className="h-4 w-4" />
											Add subtask
										</Button>
									</div>
									{childTasks.length > 0 ? (
										<ul className="space-y-1.5">
											{childTasks.map((c) => (
												<li key={c.id}>
													<button
														type="button"
														onClick={() =>
															onOpenTask(c)
														}
														className="flex w-full items-center gap-2 rounded-md border border-border px-3 py-2 text-left hover:bg-muted-subtle"
													>
														<Badge
															variant={
																STATUS_VARIANT[
																	c.status
																]
															}
															className="text-[10px]"
														>
															{stagesByKey.get(
																c.status,
															)?.name ?? c.status}
														</Badge>
														<span className="truncate text-sm text-foreground">
															{c.title}
														</span>
													</button>
												</li>
											))}
										</ul>
									) : (
										<p className="text-sm text-muted">
											No subtasks yet.
										</p>
									)}
								</section>

								<Separator />
								<ChecklistSection
									task={task}
									onToggle={onToggleChecklist}
								/>
								<GithubSection task={task} />
								<AttachmentsSection task={task} />
								<Separator />
								<CommentsSection
									task={task}
									onAdd={onAddComment}
								/>
								<Separator />
								<HistorySection task={task} />
							</div>

							<div className="border-t border-border bg-muted-subtle/30 px-5 py-5 md:border-l md:border-t-0">
								<MetaRail
									task={task}
									onStatusChange={onStatusChange}
								/>
								{task.checklist.length > 0 && (
									<p className="mt-4 text-xs text-muted">
										Checklist {checklistPct(task)}% complete
									</p>
								)}
							</div>
						</CopyTextContextMenu>
					</>
				)}
		</DrawerShell>
	);
}
